import { createContext, useContext, type ComponentType, type ReactNode } from "react";
import type { ApiClient } from "@majourneys/api-client";
import type { TransferPlace } from "@majourneys/shared";

/** Funnel steps the booking flow reports. Names are the PostHog event names. */
export type BookingTrackEvent =
  | "activity_viewed"
  | "package_selected"
  | "booking_details_started"
  | "checkout_viewed"
  | "booking_submitted"
  | "booking_completed";

export type BookingTrackProps = Record<string, string | number | boolean | null | undefined>;

/**
 * Analytics sink. The booking package never imports a tracker itself — the
 * host app (majourneys.com, each city site) decides whether and where events
 * go, so a site with no analytics simply omits it.
 */
export type BookingTrack = (event: BookingTrackEvent, props?: BookingTrackProps) => void;

export type TransferPlaceKind = "pickup" | "restaurant" | "club" | "camp";

export interface TransferPlaceFieldProps {
  kind: TransferPlaceKind;
  label: string;
  value: TransferPlace | null;
  onChange: (next: TransferPlace | null) => void;
  invalid?: boolean;
  /** Keep the label for screen readers only, when the page already shows it above the field. */
  hideLabel?: boolean;
  /**
   * The city the search leans towards (its English name, e.g. "Fez"). Left
   * out, the field searches around Marrakech, where the transfers run.
   */
  city?: string;
  /**
   * Show the "In Marrakech" / "+100 MAD" tag on each suggestion. That is the
   * transfer fee; an activity's pick-up has its own pricing, so the booking
   * sheet turns it off. Defaults to true.
   */
  showZone?: boolean;
  /**
   * Take the focus when the field appears (the restaurant ride's pickup,
   * right after the guest switches the ride on). Only on arrival.
   */
  autoFocus?: boolean;
  /**
   * Every keystroke in the search box. The booking sheet keeps what was typed
   * as the pickup address when no suggestion is tapped (website test,
   * 2026-11-01: a typed "Riad Test, Medina" was thrown away and Continue did
   * nothing). Optional: the transfer page only takes a chosen place.
   */
  onTextChange?: (text: string) => void;
}

export interface BookingContextValue {
  api: ApiClient;
  /** Origin like https://visit-fes.com or https://majourneys.com. */
  siteOrigin: string;
  /**
   * Activities that wear the pewter booking skin (see `skin.tsx`). Left out,
   * every surface keeps its classic look, which is what the city sites want.
   *
   * Kept alongside `pewterAll` so a site can still trial the skin on a
   * handful of slugs — that is how this one shipped, and it is the safe way
   * back if the full rollout has to be narrowed in a hurry.
   */
  pewterSlugs?: readonly string[];
  /**
   * Every activity wears it. The main site sets this; the city sites pass
   * nothing and stay classic, which is the whole reason the flag lives on
   * the provider rather than in the package.
   */
  pewterAll?: boolean;
  /** Optional funnel tracker; no-op when omitted. */
  track?: BookingTrack;
  /**
   * The host's icon for a category slug, or null when it has none. The
   * small confirmation drives it along the progress line; left out (the city
   * sites), the line simply has no icon on it.
   */
  categoryIconSrc?: (categorySlug: string) => string | null;
  /**
   * The host's 3D icon for a restaurant service group key ("dinner",
   * "bottle-service"), shown on L'Épicurien's Dinner / lounge cards. Left out,
   * the cards have no icon.
   */
  serviceIconSrc?: (key: string) => string | null;
  /**
   * The host's place picker (search + map pin) for transfer bookings. The
   * package never loads Google Maps itself; a site that passes nothing cannot
   * sell transfers and the form sends the guest to WhatsApp instead.
   */
  PlaceField?: ComponentType<TransferPlaceFieldProps>;
  /**
   * The host's "keep this booking in your account" card for the thank-you
   * pages. The package has no auth of its own, so the host decides whether
   * the guest is signed in and what signing in does. A site that passes
   * nothing shows nothing.
   */
  AccountPrompt?: ComponentType<AccountPromptProps>;
  /**
   * The host's "need a ride there?" card for the thank-you pages. Only a site
   * that sells transfers passes one; it decides for itself which bookings get it.
   */
  RidePrompt?: ComponentType<RidePromptProps>;
  /**
   * The host's "get the app" banner closing the thank-you pages. Only a site
   * with an app to offer passes one; city sites pass nothing and show nothing.
   */
  AppPrompt?: ComponentType<AppPromptProps>;
  /**
   * The host's cookie question, shown just above every "send" button for a
   * guest who never answered the cookie banner. The package knows nothing of
   * consent, so the host decides when it shows and what an answer does. A site
   * that passes nothing shows nothing.
   */
  ConsentPrompt?: ComponentType;
  /**
   * Where a question for the host's AI goes, as a link (majourneys.com: the
   * MaJourneys AI page with the question filled in). When set, the help card
   * offers it outside support hours instead of only "back at 10:00". A site
   * without an AI passes nothing and keeps the WhatsApp card as it was.
   */
  aiAskHref?: (query: string, lang: string) => string;
}

/** What a thank-you page knows about the booking, for the host's ride prompt. */
export interface RidePromptProps {
  kind: "transfer" | "restaurant" | "nightclub" | "stay" | "experience";
  status: string;
  slug: string;
  citySlug: string | null;
  title: string;
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM, when the booking has one. */
  time: string | null;
  /** Adults and children on the booking: who rides. */
  people: number;
  /** The booking already carries a pick-up add-on (hotel pick-up bought with it). */
  hasPickup: boolean;
}

/** What a thank-you page knows about the booking, for the host's app banner. */
export interface AppPromptProps {
  slug: string;
  citySlug: string | null;
}

/**
 * What a thank-you page knows about the booking, for the host's account
 * prompt. `email` is the address the booking was made with, when known.
 */
export interface AccountPromptProps {
  reference: string;
  email: string | null;
  title: string;
}

const BookingContext = createContext<BookingContextValue | null>(null);

export interface BookingProviderProps extends BookingContextValue {
  children: ReactNode;
}

export function BookingProvider({
  api,
  siteOrigin,
  pewterSlugs,
  pewterAll,
  track,
  categoryIconSrc,
  serviceIconSrc,
  PlaceField,
  AccountPrompt,
  RidePrompt,
  AppPrompt,
  ConsentPrompt,
  aiAskHref,
  children,
}: BookingProviderProps) {
  return (
    <BookingContext.Provider
      value={{ api, siteOrigin, pewterSlugs, pewterAll, track, categoryIconSrc, serviceIconSrc, PlaceField, AccountPrompt, RidePrompt, AppPrompt, ConsentPrompt, aiAskHref }}
    >
      {children}
    </BookingContext.Provider>
  );
}

/** The context if a provider is mounted, else null. For readers with a sane default. */
export function useOptionalBookingContext(): BookingContextValue | null {
  return useContext(BookingContext);
}

export function useBookingContext(): BookingContextValue {
  const ctx = useContext(BookingContext);
  if (!ctx) {
    throw new Error("useBookingContext must be used inside <BookingProvider>");
  }
  return ctx;
}

const noopTrack: BookingTrack = () => {};

/**
 * Tracker that is always safe to call. Unlike `useBookingContext` it does not
 * throw outside a provider, so pages stay renderable in isolation (tests,
 * storybook-style labs) and a site without analytics costs nothing.
 */
export function useBookingTrack(): BookingTrack {
  const ctx = useContext(BookingContext);
  return ctx?.track ?? noopTrack;
}

/**
 * The host's transfer place-field component, or null when the host hasn't
 * wired one up (no `PlaceField` on the provider, or no provider at all).
 */
export function useTransferPlaceField(): ComponentType<TransferPlaceFieldProps> | null {
  return useOptionalBookingContext()?.PlaceField ?? null;
}

/** The host's ride prompt for the thank-you pages, or null when it has none. */
export function useRidePrompt(): ComponentType<RidePromptProps> | null {
  return useOptionalBookingContext()?.RidePrompt ?? null;
}

/** The host's app banner for the thank-you pages, or null when it has none. */
export function useAppPrompt(): ComponentType<AppPromptProps> | null {
  return useOptionalBookingContext()?.AppPrompt ?? null;
}

/** The host's account prompt for the thank-you pages, or null when it has none. */
export function useAccountPrompt(): ComponentType<AccountPromptProps> | null {
  return useOptionalBookingContext()?.AccountPrompt ?? null;
}

/**
 * The host's cookie question for the send buttons, rendered in place. Renders
 * nothing when the host has none (city sites) or there is no provider.
 */
export function BookingConsentPrompt() {
  const Prompt = useOptionalBookingContext()?.ConsentPrompt;
  return Prompt ? <Prompt /> : null;
}
